import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import localeCore from "../../core/locale.core";
import { listInventoryForWarehouses } from "../../core/inventory.core";
import { loadProducts } from "../../core/product.core";
import { useTranslation } from "../../core/translation.core";
import MinusIcon from "../../icons/posicons/minus.icon";
import PlusIcon from "../../icons/posicons/plus.icon";
import TrashIcon from "../../icons/trash.icon";
import { InventoryList } from "../../types/inventory";
import { Product } from "../../types/product";

export interface TransferLineDraft {
  productId: string;
  quantity: number;
  name?: string;
  sku?: string;
}

type Props = {
  lines: TransferLineDraft[];
  onLinesChange: (lines: TransferLineDraft[]) => void;
  disabled?: boolean;
  fromWarehouseId?: string;
  toWarehouseId?: string;
};

function countAtWarehouse(inv: InventoryList[] | undefined, productId: string, warehouseId: string): number | undefined {
  if (!inv) return undefined;
  const row = inv.find((r) => String(r.productId) === String(productId));
  if (!row) return undefined;
  const wh = row.inventories.find((w) => String(w.warehouseId) === String(warehouseId));
  return wh?.count ?? 0;
}

export default function TransferLineItemsComp({ lines, onLinesChange, disabled, fromWarehouseId, toWarehouseId }: Props) {
  const { t, msg } = useTranslation();
  const [searchInput, setSearchInput] = useState("");
  const [activeSearch, setActiveSearch] = useState("");

  const { data: searchRes, isFetching } = useQuery({
    queryKey: ["warehouseTransferProductSearch", activeSearch],
    queryFn: () =>
      loadProducts({
        search: activeSearch,
        paging: { page: 1, size: 30 },
        includeVariationChildren: true,
      }),
    enabled: activeSearch.trim().length >= 2,
  });

  const selectableProducts = useMemo(() => searchRes?.list.filter((p) => p.productType !== "variationParent") ?? [], [searchRes]);

  const parentOnlyResults = useMemo(
    () => Boolean(searchRes?.list.length && searchRes.list.every((p) => p.productType === "variationParent")),
    [searchRes]
  );

  const inventoryProductKey = useMemo(
    () => [...new Set(lines.map((l) => l.productId).filter(Boolean))].slice().sort((a, b) => (a > b ? 1 : a < b ? -1 : 0)),
    [lines]
  );

  const canLoadLineInventory =
    Boolean(fromWarehouseId?.trim()) && Boolean(toWarehouseId?.trim()) && inventoryProductKey.length > 0;

  const { data: lineWarehouseInventory, isFetching: inventoryFetching } = useQuery({
    queryKey: ["warehouseTransferLineInventory", fromWarehouseId, toWarehouseId, inventoryProductKey.join(",")],
    queryFn: () => listInventoryForWarehouses(inventoryProductKey, [fromWarehouseId!, toWarehouseId!]),
    enabled: canLoadLineInventory,
  });

  function productDisplayLabelForSearch(p: Product) {
    return localeCore.getLocaleValueDefaultFallback(p.name) || p.sku || p.id;
  }

  function addProduct(p: Product) {
    if (disabled) return;
    if (p.productType === "variationParent") return;
    if (lines.some((l) => l.productId === p.id)) return;
    const sku =
      typeof p.sku === "string" ? p.sku.trim() || undefined : p.sku != null ? String(p.sku).trim() || undefined : undefined;
    const localized = localeCore.getLocaleValueDefaultFallback(p.name)?.trim();
    onLinesChange([
      ...lines,
      {
        productId: p.id,
        quantity: 1,
        ...(localized ? { name: localized } : {}),
        ...(sku ? { sku } : {}),
      },
    ]);
    setSearchInput("");
    setActiveSearch("");
  }

  function updateQty(index: number, quantity: number) {
    if (disabled || quantity < 1 || Number.isNaN(quantity)) return;
    const next = lines.slice();
    next[index] = { ...next[index], quantity };
    onLinesChange(next);
  }

  function adjustQty(index: number, delta: number) {
    const line = lines[index];
    if (!line || disabled) return;
    updateQty(index, line.quantity + delta);
  }

  function removeLine(index: number) {
    if (disabled) return;
    onLinesChange(lines.filter((_, j) => j !== index));
  }

  return (
    <div className="space-y-4" data-role="warehouse-transfer-lines">
      {!disabled && (
        <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
          <div className="flex-1 flex flex-col gap-1 mt-2">
            <label>{t(msg.warehouseTransfer.searchProducts)}</label>
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  setActiveSearch(searchInput.trim());
                }
              }}
              className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-her-darkblue2 px-3 py-2"
              placeholder={t(msg.posMenuItems.searchProducts)}
              data-role="warehouse-transfer-product-search"
            />
          </div>
          <button
            type="button"
            className="rounded-lg bg-her-blue text-white px-4 py-2 font-medium disabled:opacity-50"
            disabled={searchInput.trim().length < 2}
            onClick={() => setActiveSearch(searchInput.trim())}
            data-role="warehouse-transfer-product-search-btn"
          >
            {t(msg.form.add)}
          </button>
        </div>
      )}

      {activeSearch.length >= 2 && !disabled && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-600 max-h-48 overflow-y-auto bg-white dark:bg-her-darkblue2">
          {isFetching && <div className="p-3 text-sm opacity-70">{t(msg.form.loading)}</div>}
          {!isFetching && searchRes && searchRes.list.length === 0 && (
            <div className="p-3 text-sm opacity-70">{t(msg.posMenuItems.searchNoFound)}</div>
          )}
          {!isFetching && searchRes && parentOnlyResults && (
            <div className="p-3 text-sm opacity-80" data-role="warehouse-transfer-search-no-selectable-products">
              {t(msg.warehouseTransfer.parentProductNotForTransfer)}
            </div>
          )}
          {!isFetching &&
            !parentOnlyResults &&
            selectableProducts.map((p) => (
              <button
                key={p.id}
                type="button"
                data-role="warehouse-transfer-search-result-item"
                className="w-full text-left px-3 py-2 hover:bg-her-lightgray dark:hover:bg-her-bluegray/40 border-b border-gray-100 dark:border-gray-700 last:border-0"
                onClick={() => addProduct(p)}
              >
                <span className="font-medium">{productDisplayLabelForSearch(p)}</span>
                {p.sku ? <span className="text-xs opacity-70 ml-2">{p.sku}</span> : null}
              </button>
            ))}
        </div>
      )}

      <div className="space-y-2">
        <h3 className="font-semibold">{t(msg.warehouseTransfer.lineItems)}</h3>
        {lines.length === 0 ? (
          <p className="text-sm opacity-70">{t(msg.warehouseTransfer.noLineItems)}</p>
        ) : (
          <ul className="space-y-2">
            {lines.map((line, index) => {
              const sku = line.sku?.trim();
              const nameText = line.name?.trim();
              const headline = nameText || sku || line.productId;
              const showSkuRow = Boolean(sku && (!nameText || sku !== nameText));

              return (
                <li
                  key={`${line.productId}-${index}`}
                  className="flex flex-wrap items-center gap-2 rounded-lg bg-white dark:bg-her-darkblue2 p-3 border border-gray-200 dark:border-gray-600"
                  data-role={`warehouse-transfer-line-${index}`}
                >
                  <div className="flex-1 min-w-[160px] space-y-1">
                    <span className="block font-medium" data-role={`warehouse-transfer-line-name-${index}`}>
                      {headline}
                    </span>
                    {showSkuRow ? (
                      <span className="block text-xs opacity-75" data-role={`warehouse-transfer-line-sku-${index}`}>
                        {t(msg.product.sku)} {sku}
                      </span>
                    ) : null}
                    {canLoadLineInventory && fromWarehouseId && toWarehouseId ? (
                      <p className="text-xs opacity-75" data-role={`warehouse-transfer-line-stock-${index}`}>
                        {inventoryFetching ? (
                          <span>{t(msg.form.loading)}</span>
                        ) : (
                          <>
                            <span>
                              {t(msg.warehouseTransfer.fromWarehouse)}
                              {": "}
                              {countAtWarehouse(lineWarehouseInventory, line.productId, fromWarehouseId) ?? "–"}
                            </span>
                            <span className="mx-1.5 opacity-40">·</span>
                            <span>
                              {t(msg.warehouseTransfer.toWarehouse)}
                              {": "}
                              {countAtWarehouse(lineWarehouseInventory, line.productId, toWarehouseId) ?? "–"}
                            </span>
                          </>
                        )}
                      </p>
                    ) : null}
                  </div>
                  <label className="flex items-center gap-2 text-sm ml-auto">
                    {t(msg.formLabels.quantity)}
                    <span className="inline-flex items-stretch rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden bg-white dark:bg-her-darkblue2">
                      <button
                        type="button"
                        className="w-9 shrink-0 flex items-center justify-center bg-her-lightgray dark:bg-her-bluegray/50 hover:bg-her-blue hover:text-white disabled:opacity-40"
                        aria-label={`${t(msg.formLabels.quantity)} −`}
                        disabled={disabled || line.quantity <= 1}
                        onClick={() => adjustQty(index, -1)}
                        data-role={`warehouse-transfer-line-qty-minus-${index}`}
                      >
                        <MinusIcon className="w-4 h-4" />
                      </button>
                      <input
                        type="number"
                        min={1}
                        className="w-[3.25rem] min-w-0 border-0 px-2 py-1 text-center bg-white dark:bg-her-darkblue2 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        disabled={disabled}
                        value={line.quantity}
                        onChange={(e) => updateQty(index, Number(e.target.value))}
                        data-role={`warehouse-transfer-line-qty-${index}`}
                      />
                      <button
                        type="button"
                        className="w-9 shrink-0 flex items-center justify-center bg-her-lightgray dark:bg-her-bluegray/50 hover:bg-her-blue hover:text-white disabled:opacity-40"
                        aria-label={`${t(msg.formLabels.quantity)} +`}
                        disabled={disabled}
                        onClick={() => adjustQty(index, 1)}
                        data-role={`warehouse-transfer-line-qty-plus-${index}`}
                      >
                        <PlusIcon className="w-4 h-4" />
                      </button>
                    </span>
                  </label>
                  {!disabled && (
                    <button
                      type="button"
                      className="text-red-600 hover:text-red-700 p-1 shrink-0"
                      onClick={() => removeLine(index)}
                      aria-label={t(msg.form.delete)}
                      data-role={`warehouse-transfer-line-remove-${index}`}
                    >
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
